import { Upload } from 'tus-js-client';

/** Chunks keep each request well under proxy limits and make pauses cheap to resume. */
const CHUNK_SIZE_BYTES = 32 * 1024 * 1024;
const RETRY_DELAYS_MS = [0, 1_000, 3_000, 5_000, 10_000, 20_000];

export interface TusUploadCallbacks {
  onProgress(bytesSent: number, bytesTotal: number): void;
  onSuccess(): void;
  onError(error: Error): void;
}

export interface TusUploadRequest extends TusUploadCallbacks {
  readonly file: File;
  readonly endpoint: string;
  readonly metadata: Readonly<Record<string, string>>;
}

export interface TusUploadHandle {
  /** Stops sending; the server keeps the bytes received so far. */
  pause(): Promise<void>;
  /** Continues from the last byte the server confirmed. */
  resume(): void;
  /** Stops and asks the server to discard the upload. */
  cancel(): Promise<void>;
}

/**
 * Starts (or resumes) a tus upload. tus-js-client remembers upload URLs by file fingerprint in
 * localStorage, so picking the same file again after a reload continues where it stopped.
 */
export const startTusUpload = async (request: TusUploadRequest): Promise<TusUploadHandle> => {
  const upload = new Upload(request.file, {
    endpoint: request.endpoint,
    metadata: { ...request.metadata },
    chunkSize: CHUNK_SIZE_BYTES,
    retryDelays: RETRY_DELAYS_MS,
    storeFingerprintForResuming: true,
    removeFingerprintOnSuccess: true,
    onProgress: request.onProgress,
    onSuccess: () => request.onSuccess(),
    onError: (error) => request.onError(error),
  });
  // Only an earlier attempt of this very upload (same metadata, e.g. the same asset) is resumed.
  const previous = await upload.findPreviousUploads();
  const resumable = previous.find(
    (candidate) =>
      candidate.size === request.file.size &&
      Object.entries(request.metadata).every(([key, value]) => candidate.metadata[key] === value),
  );
  if (resumable) upload.resumeFromPreviousUpload(resumable);
  upload.start();
  return {
    pause: () => upload.abort(false),
    resume: () => upload.start(),
    cancel: () => upload.abort(true),
  };
};
